import { parse, type JsxAST } from 'react-native-svg';

/**
 * Parsed-SVG cache for shadowless kits (specs/challenge-flow-redesign AC-3.8).
 *
 * A kit is a ~30 KB SVG. `SvgXml` re-parses that XML in JS on every mount, so switching days in
 * the create list (two kits per match, all remounted) parsed megabytes of identical XML per tap
 * on the phone. Kits with the same type, colours and number are identical, so they share one
 * parsed tree. Shadowed (hero) kits carry a per-instance filter id and are never cached.
 */
export const KIT_AST_CACHE_MAX_ENTRIES = 200;

const cache = new Map<string, JsxAST | null>();

export function kitAstCacheKey(
  kitType: string,
  primary: string,
  secondary: string,
  number: number | undefined,
): string {
  return `${kitType}|${primary}|${secondary}|${number ?? ''}`;
}

/**
 * Returns the parsed tree for `key`, building the XML and parsing it only on a miss.
 * A parse failure is cached as `null` (renders nothing, like `SvgXml` after its onError).
 */
export function getCachedKitAst(
  key: string,
  buildXml: () => string,
  parseXml: (xml: string) => JsxAST | null = parse,
): JsxAST | null {
  if (cache.has(key)) return cache.get(key) ?? null;
  let ast: JsxAST | null;
  try {
    ast = parseXml(buildXml());
  } catch {
    ast = null;
  }
  if (cache.size >= KIT_AST_CACHE_MAX_ENTRIES) {
    // Oldest first (Map keeps insertion order); a team list rarely holds more than a few dozen.
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, ast);
  return ast;
}

export function clearKitAstCacheForTests(): void {
  cache.clear();
}
